import { Component, OnInit } from '@angular/core';
import {
	ExportDataRequest,
	ExportPage,
	FilterChangeModel,
	FilterOperator,
	FilterType,
	LogService,
	PagedQueryParameter,
	PaginationModel,
	ReportBaseComponent,
	ReportColumn,
	ReportColumnSortDirection,
	ReportingViews,
	ReportSettingDefaultDataDto,
	ReportSettings,
	TableColumn
} from '@nstep-common/core';
import { toast } from '@nstep-common/utils';
import { ReportingTableItem } from '@nstep-public/pages';
import { GridService, ReportingService, OperatorService } from '@nstep-public/shared';
import { camelCase, chain, startCase } from 'lodash';
import { forkJoin } from 'rxjs';

@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	standalone: false
})
export class ReportsComponent extends ReportBaseComponent implements OnInit {

	ReportingViews = ReportingViews;
	currentView = ReportingViews.Main;
	loading = false;

	tableData: any[] = [];
	tableDataReady = false;

	tableColumns: TableColumn[] = [
		{ name: 'Name', key: 'name', sortAsc: true, isHeaderCentered: true },
		{ name: 'Description', key: 'description', isHeaderCentered: true }
	];

	mainPagedQueryModel = new PagedQueryParameter({
		itemsPerPage: 15,
		page: 1,
		orderField: '',
		searchBy: '',
		isMultiWordSerch: false
	});

	mainPagination = new PaginationModel();

	selectedTableItem = new ReportingTableItem();
	reportSettings = new ReportSettings();

	reportData: any[][] | null = null;
	reportDataReady = false;
	reportColumns: ReportColumn[] = [];

	reportPagedQueryModel = new PagedQueryParameter({
		page: 1,
		itemsPerPage: 15,
		filterList: [],
		sortList: []
	});

	reportsPagination = new PaginationModel();
	maxRowsToExport!: number;

	constructor(private reportingService: ReportingService,
		private gridService: GridService,
		protected logger: LogService,
		private operatorService: OperatorService) {
		super(logger);
	}

	ngOnInit(): void {
		super.ngOnInit();

		this.getDefaultData();
		this.getReports();
	}

	private getDefaultData(): void {
		const getDefaultDataSubscription$ = this.reportingService
			.getDefaultData()
			.subscribe({
				next: (response: ReportSettingDefaultDataDto) => {
					this.maxRowsToExport = response.maxRowsToExport;

					const endpoints = chain(response.reportTemplates)
						.flatMap(d => d.headers)
						.filter(d => d.filterType === FilterType.List)
						.map(d => d.endpoint!)
						.uniq()
						.value();

					const observables = endpoints.map(f => this.getColumnFilterCollection(f, this.gridService));

					const subscriptionForkJoin$ = forkJoin(observables)
						.subscribe(responses => {
							const results: { [key: string]: string[] } = {};
							responses.forEach((values, index) => { results[endpoints[index]] = values; });

							response.reportTemplates.forEach(template => {

								this.reportTemplateColumns[template.id] = template.headers.map((column, index) => {
									const assignedFilters = this.getDefaultFilters(column.filterType, results[column.endpoint!], camelCase(column.name) === 'timestamp');

									const col = new ReportColumn({
										key: camelCase(column.name),
										name: startCase(column.name),
										isDate: column.isDate,
										orderNumber: index + 1,
										filterType: column.filterType,
										validOperators: column.validFilters,
										agregateOperator: column.filterType === FilterType.Basic ? FilterOperator.AND : FilterOperator.OR,
										assignedFilters: assignedFilters,
										selectedItemsCount: assignedFilters.filter(f => f.isChecked).length
									});

									return col;
								});
							});
						});

					this.subscriptions.push(subscriptionForkJoin$);
				}
			});

		this.subscriptions.push(getDefaultDataSubscription$);
	}

	private getReports(): void {
	
		const hasLocation = this.operatorService.haslocationInfo();
		let model = this.mainPagedQueryModel;
		if(hasLocation) {
			const selectedLocation = this.operatorService.getLocationInfo();
			const locationId = selectedLocation?.location.id;
			model = { ... model, locationId} as PagedQueryParameter;
		}

		const getReportsSubscription$ = this.reportingService.getReports(model)
			.subscribe({
				next: response => {

					this.mainPagination = response.page;
					this.tableData = chain(response.results)
						.map(e => new ReportingTableItem({
							id: e.id,
							name: e.name
						}))
						.value();

					this.tableDataReady = true;
				},
				error: () => {
					this.tableDataReady = true;
				}
			});

		this.subscriptions.push(getReportsSubscription$);
	}

	onEditAction(item: ReportingTableItem): void {
		this.selectedTableItem = item;
		this.reportPagedQueryModel = new PagedQueryParameter({
			page: 1,
			itemsPerPage: 15,
			filterList: [],
			sortList: []
		});

		this.loading = true;
		this.currentView = ReportingViews.Report;

		const getReportSettingsSubscription$ = this.reportingService
			.getReportSettings(this.selectedTableItem.id!)
			.subscribe({
				next: response => {
					this.reportSettings = new ReportSettings({
						templateId: response.templateId,
						canHideColumns: response.reportSettingsDetails.canHideColumns,
						canExport: response.reportSettingsDetails.canExport,
						canReorderColumns: response.reportSettingsDetails.canReorderColumns,
						canSort: response.reportSettingsDetails.canSort,
						canFilter: response.reportSettingsDetails.canFilter
					});

					const defaultTemplateColumns = this.reportTemplateColumns[this.reportSettings.templateId!];
					const colData = this.getColumnData(defaultTemplateColumns, response.reportSettingsDetails.columns);

					this.reportColumns = [...colData.reportColumns];
					this.reportPagedQueryModel.sortList.push(...colData.sortList);
					this.reportPagedQueryModel.filterList.push(...colData.filterList);

					this.getReportTemplate(this.reportSettings.templateId);
				}
			});

		this.subscriptions.push(getReportSettingsSubscription$);
	}

	private getReportTemplate(templateId: number): void {
		const getReportTemplateSubscription$ = this.reportingService
			.getReportTemplate(templateId, this.reportPagedQueryModel)
			.subscribe({
				next: response => {
					this.reportsPagination = response.page;
					this.reportData = this.getTemplatePaginatedData(response.results);

					this.loading = false;
					this.reportDataReady = true;
				},
				error: () => {
					this.reportDataReady = true;
					this.loading = false;
				}
			});

		this.subscriptions.push(getReportTemplateSubscription$);
	}

	mainPageChange(page: PaginationModel): void {
		this.mainPagedQueryModel.page = page.currentPage;
		this.mainPagedQueryModel.itemsPerPage = page.pageSize;

		this.tableDataReady = false;
		this.tableData = [];

		this.getReports();
	}

	reportsPageChange(page: PaginationModel): void {
		this.reportPagedQueryModel.page = page.currentPage;
		this.reportPagedQueryModel.itemsPerPage = page.pageSize;

		this.reportDataReady = false;
		this.reportData = null;

		this.getReportTemplate(this.reportSettings.templateId!);
	}

	exportExcelTemplate(isPartialDownload: boolean ): void {
		this.loading = true;

		this.reportingService.invalidateDefaultDataCache();

		const trimRows = !isPartialDownload && this.reportsPagination.totalCount > this.maxRowsToExport;
		if (trimRows) {
			toast('Warning', `The file will only export a maximum of ${this.maxRowsToExport} rows.`, 'orange');
		}

		const model = new ExportDataRequest({
			reportName: this.selectedTableItem.name!,
			columns: this.reportColumns.filter(item => item.isShown).map(d => d.name.replace(/\s/g, '')),
			filterList: this.reportPagedQueryModel.filterList,
			sortList: this.reportPagedQueryModel.sortList,
			exportPage: isPartialDownload || trimRows ? new ExportPage({
				page: trimRows ? 1 : this.reportPagedQueryModel.page,
				itemsPerPage: trimRows ? this.maxRowsToExport : this.reportPagedQueryModel.itemsPerPage
			}) : undefined
		});

		const exportReportTemplateSubscription$ = this.reportingService.exportReportTemplate(this.reportSettings.templateId!, model).subscribe({
			next: response => {
				const blob: Blob = response.body as Blob;
				const fileName = response.headers.get('Content-Disposition')?.replace(/['"]+/g, '').split(';')[1].trim().split('=')[1];

				const anchor = document.createElement('a');
				anchor.download = fileName;
				anchor.href = window.URL.createObjectURL(blob);
				anchor.click();

				this.loading = false;
				toast('Success', 'File exported successfully.', 'green');
			},
			error: () => {
				this.loading = false;
				toast('Error', 'Failed to export file.', 'red');
			}
		});

		this.subscriptions.push(exportReportTemplateSubscription$);
	}

	sortByCol(column: ReportColumnSortDirection): void {
		this.reportDataReady = false;
		this.reportData = null;
		this.processColumnSortation(column, this.reportColumns, this.reportPagedQueryModel);

		this.getReportTemplate(this.reportSettings.templateId!);
	}

	filterChange(value: FilterChangeModel): void {
		this.reportDataReady = false;
		this.reportData = null;
		this.proccessColumnFilters(value, this.reportPagedQueryModel);

		this.getReportTemplate(this.reportSettings.templateId!);
	}

	search(): void {
		this.mainPagedQueryModel.page = 1;
		this.tableDataReady = false;
		this.tableData = [];

		this.getReports();
	}
}
